import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined'
import { Box, Divider, Menu, MenuItem } from '@mui/material'
import { ReactNode } from 'react'

import { useAppContext } from '@/hooks'

import { canEditBranchDefinition } from '../branchDefinition'
import { canRebaseBranch } from '../rebaseBranch'
import { BranchRecord } from '../types'

export type BranchMenuAnchor = {
	anchor: HTMLElement
	branch: BranchRecord
}

type BranchContextMenuProps = {
	activeBranchId: string | null
	canManage: boolean
	ctx: BranchMenuAnchor | null
	onClose: () => void
	onCompare: (branch: BranchRecord) => void
	onCreate: (branch: BranchRecord) => void
	onCreateMerge: (branch: BranchRecord) => void
	onDelete: (branch: BranchRecord) => void
	onEditDefinition: (branch: BranchRecord) => void
	onRebase: (branch: BranchRecord) => void
	onSetActive: (branchId: string) => void
	onSetStatus: (branch: BranchRecord, status: BranchRecord['status']) => void
	onToggleProtected: (branch: BranchRecord) => void
}

const Item = ({
	danger,
	disabled,
	icon,
	label,
	onClick,
}: {
	danger?: boolean
	disabled?: boolean
	icon: ReactNode
	label: string
	onClick: () => void
}) => (
	<MenuItem
		disabled={disabled}
		onClick={onClick}
		sx={{
			fontSize: 12.5,
			gap: '9px',
			color: danger ? '#C5221F' : '#3D3D3D',
			py: '7px',
		}}
	>
		<Box
			sx={{
				display: 'flex',
				color: danger ? '#C5221F' : '#7A7A7A',
			}}
		>
			{icon}
		</Box>
		{label}
	</MenuItem>
)

export const BranchContextMenu = ({
	activeBranchId,
	canManage,
	ctx,
	onClose,
	onCompare,
	onCreate,
	onCreateMerge,
	onDelete,
	onEditDefinition,
	onRebase,
	onSetActive,
	onSetStatus,
	onToggleProtected,
}: BranchContextMenuProps) => {
	const { t } = useAppContext()
	const branch = ctx?.branch

	const run = (action: () => void) => {
		action()
		onClose()
	}

	return (
		<Menu
			anchorEl={ctx?.anchor ?? null}
			open={Boolean(ctx?.anchor)}
			onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			slotProps={{ paper: { sx: { minWidth: 224, borderRadius: '10px', mt: '2px' } } }}
		>
			{branch ? (
				<Box
					sx={{
						px: '12px',
						py: '6px',
						fontSize: '10px',
						fontWeight: 700,
						letterSpacing: '.05em',
						textTransform: 'uppercase',
						color: '#9A9A9A',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}
				>
					{branch.name}
				</Box>
			) : null}
			<Divider sx={{ mb: '2px' }} />
			{branch && canEditBranchDefinition(branch.status) ? (
				<Item
					icon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
					label={t('BRANCHES_EDIT_DEFINITION')}
					onClick={() => run(() => onEditDefinition(branch))}
				/>
			) : null}
			{branch && canRebaseBranch(branch) ? (
				<Item
					icon={<SyncAltOutlinedIcon sx={{ fontSize: 15 }} />}
					label={t('BRANCHES_REBASE')}
					onClick={() => run(() => onRebase(branch))}
				/>
			) : null}
			<Item
				disabled={branch?.status === 'DELETED'}
				icon={<AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />}
				label={t('BRANCHES_CREATE')}
				onClick={() => branch && run(() => onCreate(branch))}
			/>
			<Item
				disabled={branch?.status === 'DELETED'}
				icon={<CallSplitOutlinedIcon sx={{ fontSize: 15 }} />}
				label={t('MERGE_REQUESTS_CREATE')}
				onClick={() => branch && run(() => onCreateMerge(branch))}
			/>
			{branch && activeBranchId !== branch.id ? (
				<Item
					disabled={branch.status === 'DELETED'}
					icon={<ArrowForwardOutlinedIcon sx={{ fontSize: 15 }} />}
					label={t('BRANCHES_SET_ACTIVE')}
					onClick={() => run(() => onSetActive(branch.id))}
				/>
			) : null}
			{branch?.status === 'LOCKED' ? (
				<Item
					disabled={!canManage}
					icon={<LockOpenOutlinedIcon sx={{ fontSize: 15 }} />}
					label={t('UNLOCK')}
					onClick={() => run(() => onSetStatus(branch, 'ACTIVE'))}
				/>
			) : (
				<Item
					disabled={!canManage}
					icon={<LockOutlinedIcon sx={{ fontSize: 15 }} />}
					label={t('LOCK')}
					onClick={() => branch && run(() => onSetStatus(branch, 'LOCKED'))}
				/>
			)}
			<Item
				disabled={!canManage}
				icon={<ArchiveOutlinedIcon sx={{ fontSize: 15 }} />}
				label={
					branch?.status === 'ARCHIVED'
						? t('BRANCHES_RESTORE_ARCHIVE')
						: t('BRANCHES_ARCHIVE')
				}
				onClick={() =>
					branch &&
					run(() =>
						onSetStatus(
							branch,
							branch.status === 'ARCHIVED' ? 'ACTIVE' : 'ARCHIVED',
						),
					)
				}
			/>
			<Item
				disabled={!canManage}
				icon={<ShieldOutlinedIcon sx={{ fontSize: 15 }} />}
				label={
					branch?.protectedBranch ? t('BRANCHES_UNPROTECT') : t('BRANCHES_PROTECT')
				}
				onClick={() => branch && run(() => onToggleProtected(branch))}
			/>
			<Divider sx={{ my: '2px' }} />
			<Item
				icon={<DifferenceOutlinedIcon sx={{ fontSize: 15 }} />}
				label={t('BRANCHES_COMPARE_WITH')}
				onClick={() => branch && run(() => onCompare(branch))}
			/>
			<Item
				danger
				disabled={!canManage}
				icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 15 }} />}
				label={t('DELETE')}
				onClick={() => branch && run(() => onDelete(branch))}
			/>
			{!canManage ? (
				<Box
					sx={{
						px: '12px',
						py: '6px',
						fontSize: '10.5px',
						color: '#B0863A',
						bgcolor: '#FEF7EA',
						lineHeight: 1.4,
						mt: '2px',
					}}
				>
					{t('BRANCHES_ADMIN_REQUIRED')}
				</Box>
			) : null}
		</Menu>
	)
}
